import { ref, reactive, type Ref } from "vue";
import {
  localStorageGetCustomerCode,
  localStorageGetAuthType,
} from "@/common/localStorageUtil";
import { checkChainListDisplay } from "@/common/business";
import { getShopList } from "@/common/graphql/shop";
import type { ShopListInput } from "@/generated/graphql";
import type { ChainItem, ShopSelect, SetFieldValue } from "../types";

interface UseShopListSearchFormParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}

/**
 * 検索フォームの入力状態と操作を提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useShopListSearchForm = (params: UseShopListSearchFormParams) => {
  const { errdialog, errMessage } = params;

  /**
   * チェーンリスト
   */
  const chainListItems = ref<
    Array<ChainItem | null | undefined> | null | undefined
  >([]);
  const chainSelectValues = ref<{ chain_code: string; name: string }>({
    chain_code: "",
    name: "",
  });

  /**
   * 店舗一覧 input
   */
  const shopListInput = reactive<ShopListInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  });

  /**
   * 店舗名の選択ボックス
   */
  const shopListItems = ref<
    Array<ShopSelect | null | undefined> | null | undefined
  >([]);
  const ShopNameSelectValues = ref<
    Array<{ shop_code: string; name: string }>
  >([]);

  //ユーザ権限取得
  const chainViewFlag = checkChainListDisplay(localStorageGetAuthType());

  /*  絞込み条件 */
  const searchConditionChainCode = ref<string | undefined | null>(null); // チェーンコード
  const searchConditionShopCode = ref<string | undefined | null>(null); // 店舗コード
  const searchConditionPhone = ref<string | undefined | null>(null); // 電話番号
  const searchConditionZipCode = ref<string | undefined | null>(null); // 郵便番号
  const searchConditionAddress = ref<string | undefined | null>(null); // 住所

  /**
   * 「チェーン」選択ボックス @change
   * resetTable: テーブル状態リセット用コールバック（循環依存回避のためindex.vueから注入）
   */
  const onChangeChain = async (
    setFieldValue: SetFieldValue,
    resetTable: () => void
  ): Promise<void> => {
    try {
      setFieldValue("shop_name", []);
      ShopNameSelectValues.value = [];
      shopListItems.value = [];

      // テーブル状態リセット（コールバック経由）
      resetTable();

      //チェーン名が空の場合、店舗、エリア名も空にする
      if (!chainSelectValues.value) {
        return;
      }

      //チェーンコードを設定
      searchConditionChainCode.value = chainSelectValues.value.chain_code;
      shopListInput.chain_code = chainSelectValues.value.chain_code;

      //店舗情報を取得する
      shopListItems.value = await getShopList(shopListInput);
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    }
  };

  return {
    chainListItems,
    chainSelectValues,
    shopListInput,
    shopListItems,
    ShopNameSelectValues,
    chainViewFlag,
    searchConditionChainCode,
    searchConditionShopCode,
    searchConditionPhone,
    searchConditionZipCode,
    searchConditionAddress,
    onChangeChain,
  };
};
